import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Alert
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import MaterialReactTable from 'material-react-table';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  FilterListOff as FilterListOffIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { UrlApi } from '../../../services/apirest';

const ExclusionesIngresosBrutos = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedClient, setSelectedClient] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    const fetchExclusiones = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${UrlApi}/excenciones/all`);
        
        if (!response.ok) {
          throw new Error('Error en la respuesta del servidor');
        }

        const result = await response.json();
        setData(result);
        setLoading(false);

      } catch (err) {
        console.error(err);
        setError('Ocurrió un error al cargar las exclusiones de Ingresos Brutos.');
        setLoading(false);
      }
    };

    fetchExclusiones();
  }, []);

  useEffect(() => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      setDateError('La fecha "Desde" no puede ser posterior a la fecha "Hasta".');
    } else {
      setDateError('');
    }
  }, [dateFrom, dateTo]);

  const parseLocalDate = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('-');
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    return new Date(year, month, day);
  };

  const calcularEstado = (vtoString) => {
    const vtoDate = parseLocalDate(vtoString);
    if (!vtoDate) return { label: 'Desconocido', tipo: 'default' };

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    const vtoClean = new Date(vtoDate.getFullYear(), vtoDate.getMonth(), vtoDate.getDate());

    if (vtoClean < hoy) {
      return { label: 'Vencida', tipo: 'vencida' };
    }

    const esMismoMesYAno =
      vtoClean.getFullYear() === hoy.getFullYear() &&
      vtoClean.getMonth() === hoy.getMonth();

    if (esMismoMesYAno) {
      return { label: 'Vence este mes', tipo: 'vence_este_mes' };
    }

    return { label: 'Vigente', tipo: 'vigente' };
  };

  const clientesList = useMemo(() => {
    const unicos = new Map();
    data.forEach((item) => {
      if (item.razon_social && !unicos.has(item.razon_social)) {
        unicos.set(item.razon_social.trim(), { cliente: item.razon_social.trim(), cuit: item.cuit_limpio });
      }
    });
    return Array.from(unicos.values());
  }, [data]);

  const filteredData = useMemo(() => {
    return data.filter((item) => {
        console.log(selectedClient)
        console.log(item)
      if (selectedClient && item.razon_social !== selectedClient.cliente) {
        return false;
      }

      const vtoReal = parseLocalDate(item['Vto Excencion']);
      if (!vtoReal) return true;

      if (dateFrom) {
        const [fY, fM, fD] = dateFrom.split('-').map(Number);
        const dFrom = new Date(fY, fM - 1, fD);
        if (vtoReal < dFrom) return false;
      }

      if (dateTo) {
        const [tY, tM, tD] = dateTo.split('-').map(Number);
        const dTo = new Date(tY, tM - 1, tD);
        if (vtoReal > dTo) return false;
      }

      return true;
    });
  }, [data, selectedClient, dateFrom, dateTo]);

  const handleClearFilters = () => {
    setSelectedClient(null);
    setDateFrom('');
    setDateTo('');
    setDateError('');
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'razon_social',
        header: 'Razon Social',
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1F618D' }}>
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: 'cuit_limpio',
        header: 'CUIT',
        Cell: ({ cell }) => {
          const val = cell.getValue() || '';
          const formatted = val.length === 11 ? `${val.slice(0, 2)}-${val.slice(2, 10)}-${val.slice(10)}` : val;
          return <Typography variant="body2">{formatted}</Typography>;
        },
      },
      {
        accessorKey: 'provincia',
        header: 'Provincia / Jurisdicción',
      },
      {
        accessorKey: 'porcentaje_excencion',
        header: '% Exclusión',
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {cell.getValue()}%
          </Typography>
        ),
      },
      {
        accessorKey: 'Vto Excencion',
        header: 'Vencimiento',
      },
      {
        id: 'estado',
        header: 'Estado',
        accessorFn: (row) => calcularEstado(row['Vto Excencion']).label,
        Cell: ({ row }) => {
          const estadoInfo = calcularEstado(row.original['Vto Excencion']);
          
          let chipColor = {};
          if (estadoInfo.tipo === 'vencida') {
            chipColor = { bg: '#FDEDEC', color: '#922B21', border: '#F5B7B1' };
          } else if (estadoInfo.tipo === 'vence_este_mes') {
            chipColor = { bg: '#FEF9E7', color: '#B7950B', border: '#F9E79F' };
          } else {
            chipColor = { bg: '#EAFAF1', color: '#1E8449', border: '#A9DFBF' };
          }

          return (
            <Chip
              label={estadoInfo.label}
              size="small"
              sx={{
                backgroundColor: chipColor.bg,
                color: chipColor.color,
                fontWeight: 600,
                border: `1px solid ${chipColor.border}`,
                borderRadius: '6px',
              }}
            />
          );
        },
      },
    ],
    []
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#F4F6F6', minHeight: '100vh' }}>
      <Card
        elevation={0}
        sx={{
          mb: 3,
          backgroundColor: '#1F618D',
          borderRadius: '8px',
          color: '#FFFFFF',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.08)'
        }}
      >
        <CardContent sx={{ py: '16px !important', px: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AssignmentIcon sx={{ fontSize: 28, color: '#FFFFFF' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.3px' }}>
              Exclusiones de Ingresos Brutos
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: '#164E63',
              px: 2,
              py: 0.8,
              borderRadius: '6px',
              border: '1px solid rgba(255,255,255,0.15)',
              fontSize: '0.875rem',
              fontWeight: 500
            }}
          >
            {data?.length || 0} exclusiones registradas
          </Box>
        </CardContent>
      </Card>

      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: '8px',
          border: '1px solid #D5DBDB',
          backgroundColor: '#FFFFFF',
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#164E63', mb: 2 }}>
            Filtros de Búsqueda
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={clientesList}
                getOptionLabel={(option) => option.cliente || ''}
                value={selectedClient}
                onChange={(event, newValue) => setSelectedClient(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Filtrar por Cliente" placeholder="Escriba para buscar..." size="small" fullWidth />
                )}
                noOptionsText="No se encontraron clientes"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Vencimiento Desde"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Vencimiento Hasta"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
                error={Boolean(dateError)}
                helperText={dateError}
              />
            </Grid>
            <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<FilterListOffIcon />}
                onClick={handleClearFilters}
                size="small"
                sx={{
                  borderColor: '#95A5A6',
                  color: '#566573',
                  textTransform: 'none',
                  '&:hover': { borderColor: '#7F8C8D', backgroundColor: '#F2F4F4' },
                }}
                fullWidth
              >
                Limpiar Filtros
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid #D5DBDB',
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 1px 3px rgba(0,0,0,0.04)',
          '& .MuiTableHead-root th': {
            backgroundColor: '#FAFAFA',
            fontWeight: 700,
            color: '#2C3E50',
            borderBottom: '2px solid #E5E8E8',
          }
        }}
      >
        <MaterialReactTable
          columns={columns}
          data={filteredData}
          state={{ isLoading: loading }}
          enableRowActions={false}
          enableColumnActions={false}
          enableColumnFilters={false}
          enablePagination={true}
        />
      </Box>
    </Box>
  );
};

export default ExclusionesIngresosBrutos;
